import React from "react";
import { useQuery } from "@apollo/client";
import { Link, Redirect } from "react-router-dom";
import { Alert, BreadcrumbItem, Card, CardBody, Col, Container, Row } from "reactstrap";
import * as Loading from "@repoflow/components/loading";
import * as EntryQL from "queries/entry";
import { addDays, dayLabel, isDay, today, weekdayShort } from "components/day/dates";
import { byMeal, formatNumber, macroSplit } from "components/day/summary";
import "components/day/day.css";

export const Sidebar = () => null;

export const Indice = ({ match: { params: { date } } }) => (
  <BreadcrumbItem>{isDay(date) ? dayLabel(date) : date}</BreadcrumbItem>
);

// How many days the strip across the top shows, ending on the one on screen.
const WEEK = 7;

// One day of the food log: a week strip to move between days, the day's
// totals, and what was eaten, by meal.
//
// ONE read for all of it. `days` returns every day of the range with its totals
// and its entries, so the strip and the list below are the same response and
// cannot disagree about what was logged. `cache-and-network` because the strip
// is shared between seven neighbouring screens: moving a day back shows the
// cached week at once and corrects it when the network answers.
export const Content = ({ match: { params: { date } } }) => {
  const valid = isDay(date);
  const from = valid ? addDays(date, -(WEEK - 1)) : null;

  const { data, loading, error } = useQuery(EntryQL.days, {
    variables: { input: { from, to: date } },
    skip: !valid,
    fetchPolicy: "cache-and-network",
  });

  // A typo'd or impossible day (`/day/2026-02-31`) is not an error worth a
  // screen: there is an obvious day the person meant to land on.
  if (!valid) return <Redirect to={`/day/${today()}`} />;

  const days = data?.viewer?.account?.entries?.days || [];
  const current = days.find((d) => d.date === date);

  return (
    <Container className="noshchat-day mt-4">
      <DayNav date={date} />

      {error ? (
        <Alert color={"danger"} className="mt-3">
          The food log could not be read. {error.message}
        </Alert>
      ) : null}

      {!data && loading ? (
        <div className="mt-4"><Loading.Icon /></div>
      ) : (
        <>
          <WeekStrip days={days} date={date} />
          <Totals totals={current?.totals} />
          <Meals date={date} entries={current?.entries || []} />
        </>
      )}
    </Container>
  );
};

// Two rows on a phone, one from `md` up: the title gets the whole width it
// needs and the buttons sit under it, rather than the four of them squeezing
// "Wednesday, October 21" into a column a word wide.
const DayNav = ({ date }) => (
  <Row className="align-items-center">
    <Col xs={"auto"}>
      <Link className="btn btn-outline-secondary btn-sm" to={`/day/${addDays(date, -1)}`} aria-label={"Previous day"}>
        <i className="fa fa-chevron-left" />
      </Link>
    </Col>
    <Col>
      <h4 className="m-0" data-testid={"day-title"}>{dayLabel(date)}</h4>
      <div className="text-muted small">{date}</div>
    </Col>
    <Col xs={12} md={"auto"} className="mt-2 mt-md-0 text-nowrap">
      {date !== today() ? (
        <Link className="btn btn-outline-secondary btn-sm mr-2" to={`/day/${today()}`}>
          Today
        </Link>
      ) : null}
      <Link className="btn btn-outline-secondary btn-sm mr-2" to={`/day/${addDays(date, 1)}`} aria-label={"Next day"}>
        <i className="fa fa-chevron-right" />
      </Link>
      <Link className="btn btn-primary btn-sm" to={`/day/${date}/new`}>
        <i className="fa fa-plus" /> Add food
      </Link>
    </Col>
  </Row>
);

// Seven days, the last of them the one on screen, each a link to itself with
// its calories under it. The bar height is the day's share of the busiest day
// IN THE STRIP — a relative picture of the week, not a target, because there
// is no target yet to measure against.
const WeekStrip = ({ days, date }) => {
  const max = Math.max(1, ...days.map((d) => d.totals.calories));

  return (
    <div className="noshchat-week mt-3" role={"list"} aria-label={"The last seven days"}>
      {days.map((d) => (
        <Link
          key={d.date}
          to={`/day/${d.date}`}
          role={"listitem"}
          className={`noshchat-week__day${d.date === date ? " noshchat-week__day--current" : ""}`}
          aria-current={d.date === date ? "date" : undefined}
        >
          <span className="noshchat-week__label">{weekdayShort(d.date)}</span>
          <span className="noshchat-week__bar">
            <span style={{ height: `${Math.round((d.totals.calories / max) * 100)}%` }} />
          </span>
          <span className="noshchat-week__kcal">{formatNumber(d.totals.calories)}</span>
        </Link>
      ))}
    </div>
  );
};

const Totals = ({ totals }) => {
  const t = totals || { calories: 0, protein: 0, carbs: 0, fat: 0, count: 0 };
  const split = macroSplit(t);

  return (
    <Card className="mt-3">
      <CardBody>
        <Row className="align-items-center">
          <Col xs={12} md={4}>
            <div className="noshchat-total__kcal" data-testid={"day-calories"}>
              {formatNumber(t.calories)}
            </div>
            <div className="text-muted">kcal eaten</div>
          </Col>
          <Col xs={12} md={8}>
            <Row>
              <Macro label={"Protein"} grams={t.protein} share={split.protein} kind={"protein"} />
              <Macro label={"Carbs"} grams={t.carbs} share={split.carbs} kind={"carbs"} />
              <Macro label={"Fat"} grams={t.fat} share={split.fat} kind={"fat"} />
            </Row>
            <div className="noshchat-split mt-2" aria-hidden={"true"}>
              <span className="noshchat-split--protein" style={{ width: `${split.protein}%` }} />
              <span className="noshchat-split--carbs" style={{ width: `${split.carbs}%` }} />
              <span className="noshchat-split--fat" style={{ width: `${split.fat}%` }} />
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

const Macro = ({ label, grams, share, kind }) => (
  <Col xs={4}>
    <div className="small text-muted">
      <span className={`noshchat-dot noshchat-dot--${kind}`} /> {label}
    </div>
    <div className="noshchat-total__macro" data-testid={`day-${kind}`}>
      {formatNumber(grams, 1)} g
    </div>
    <div className="small text-muted">{share}% of energy</div>
  </Col>
);

const Meals = ({ date, entries }) => (
  <>
    {byMeal(entries).map((group) => (
      <Card className="mt-3" key={group.meal} data-testid={`meal-${group.meal}`}>
        <CardBody>
          <Row className="align-items-center">
            <Col className="text-nowrap">
              <h5 className="m-0">{group.label}</h5>
            </Col>
            <Col xs={"auto"} className="text-muted px-0">
              {formatNumber(group.calories)} kcal
            </Col>
            <Col xs={"auto"}>
              <Link className="btn btn-link btn-sm" to={`/day/${date}/new?meal=${group.meal}`}>
                <i className="fa fa-plus" /> Add
              </Link>
            </Col>
          </Row>
          {group.items.length ? (
            <ul className="list-unstyled m-0 mt-2">
              {group.items.map((entry) => (
                <li key={entry.entryId} className="noshchat-entry">
                  <Link to={`/entry/${entry.entryId}`} className="noshchat-entry__link">
                    <span className="noshchat-entry__food">
                      {entry.food}
                      {entry.serving ? <span className="text-muted"> · {entry.serving}</span> : null}
                    </span>
                    <span className="noshchat-entry__macros text-muted small">
                      P {formatNumber(entry.protein, 1)} · C {formatNumber(entry.carbs, 1)} · F {formatNumber(entry.fat, 1)}
                    </span>
                    <span className="noshchat-entry__kcal">{formatNumber(entry.calories)} kcal</span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : null}
        </CardBody>
      </Card>
    ))}
  </>
);
